import { useMutualFundDetailScrollEvent } from '../../hooks/useMutualFundDetailScrollEvent'
import {
  EliteScoresTypes,
  MutualFundDetailSections,
  WaterfallSections,
  WaterfallValues
} from '../../types/mutual-fund-detail.types'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import AutoPlayRiveComponent from '../elite/AutoPlayRiveComponent'
import HelpButton from '../ui-components/HelpButton'
import RenderHTML from '../ui-components/RenderHTML'
import useAlternateISIN from '../../hooks/useAlternateISIN'
import { postCTA, trackEvent } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'
import { useMetaDataContext } from '../../state-management/contexts/MetaDataContext'
import EliteCard from './EliteCard'
import { ReturnsScoreHelpContent } from '../BottomSheetComponents/ReturnsScoreHelpContent'
import { StabilityScoreHelpContent } from '../BottomSheetComponents/StabilityScoreHelpContent'
import { RecencyScoreHelpContent } from '../BottomSheetComponents/RecencyScoreHelpContent'
import { getAppVersion } from '../../utils/sessionStorage'
import RiveComponent, { RiveFit } from '../RiveComponent'
import { ELITE_RIVE_URL } from '../../constants'

interface EliteFeaturesRowProps {
  featureIcon: string
  featureName: string
  helpContent: React.FC<{ onDismiss: () => void }>
}

type FeatureType = {
  featureIcon: string
  helpContent: React.FC<{ onDismiss: () => void }>
  eliteScoresType: `${EliteScoresTypes}`
  isLast?: boolean
}

const features: FeatureType[] = [
  {
    featureIcon: '/images/mf-l2/returns-score.svg',
    helpContent: ReturnsScoreHelpContent,
    eliteScoresType: EliteScoresTypes.CONSISTENCY
  },
  {
    featureIcon: '/images/mf-l2/recency-score.svg',
    helpContent: RecencyScoreHelpContent,
    eliteScoresType: EliteScoresTypes.RECENCY
  },
  {
    featureIcon: '/images/mf-l2/stability-score.svg',
    helpContent: StabilityScoreHelpContent,
    eliteScoresType: EliteScoresTypes.VOLATILITY,
    isLast: true
  }
]

// Helper function to get feature name based on app version
const getFeatureName = (eliteScoresType: `${EliteScoresTypes}`): string => {
  switch (eliteScoresType) {
    case EliteScoresTypes.CONSISTENCY:
      return getAppVersion() >= 47 ? 'Consistency Score' : 'Returns Score'
    case EliteScoresTypes.RECENCY:
      return 'Recency Score'
    case EliteScoresTypes.VOLATILITY:
      return getAppVersion() >= 47 ? 'Volatility Score' : 'Stability Score'
    default:
      return ''
  }
}

const LockIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
  <path d="M11.5 6.67578H2.5C1.94772 6.67578 1.5 7.1235 1.5 7.67578V13.6758C1.5 14.2281 1.94772 14.6758 2.5 14.6758H11.5C12.0523 14.6758 12.5 14.2281 12.5 13.6758V7.67578C12.5 7.1235 12.0523 6.67578 11.5 6.67578Z" stroke="#8E9393" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M10.5 6.67578V5.17578C10.5 4.24752 10.1313 3.35728 9.47487 2.70091C8.8185 2.04453 7.92826 1.67578 7 1.67578C6.07174 1.67578 5.1815 2.04453 4.52513 2.70091C3.86875 3.35728 3.5 4.24752 3.5 5.17578V6.67578" stroke="#8E9393" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const FilledLockIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
  <path d="M19.7161 10.4312H4.28753C3.34076 10.4312 2.57324 11.1987 2.57324 12.1454V22.4312C2.57324 23.378 3.34076 24.1454 4.28753 24.1454H19.7161C20.6629 24.1454 21.4304 23.378 21.4304 22.4312V12.1454C21.4304 11.1987 20.6629 10.4312 19.7161 10.4312Z" fill="#722200" stroke="#722200" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M18 10.4293V7.85791C18 6.26661 17.3679 4.74048 16.2426 3.61528C15.1174 2.49005 13.5913 1.85791 12 1.85791C10.4087 1.85791 8.88257 2.49005 7.75737 3.61528C6.63214 4.74048 6 6.26661 6 7.85791V10.4293" stroke="#722200" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const EliteFeaturesRow = ({
  featureIcon,
  featureName,
  helpContent: HelpContent,
  isLast = false
}: EliteFeaturesRowProps & { helpContent: React.FC<{ onDismiss: () => void }>; isLast?: boolean }) => {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  return (
    <div
      className={`flex w-full flex-row justify-between px-[20px] py-[18px] ${!isLast ? 'border-b border-[#EBF2F2]' : ''}`}
    >
      <div className='flex flex-row gap-4 items-center'>
        <div className='icon'>
          <img src={featureIcon} />
        </div>
        <div className='h-full flex justify-center flex-col gap-[1px]'>
          <div className='text-[13px] text-[#6A6E6E] leading-[16px]'>{featureName}</div>
          <div className='flex items-center text-center text-[18px] text-[#6A6E6E] font-[400]'>
            <span>
              <RenderHTML innerHTMLText={LockIcon} />
            </span>
            <span className='pl-[1px]'>/100</span>
          </div>
        </div>
      </div>
      <HelpButton
        aria-label={`Help about ${featureName}`}
        onClick={() => openBottomSheet(<HelpContent onDismiss={closeBottomSheet} />)}
      />
    </div>
  )
}

export default function NewPreEliteCard({ sectionKey }: { sectionKey: `${MutualFundDetailSections}` }) {
  const { isUserElite, data } = useMetaDataContext()
  const sectionRef = useMutualFundDetailScrollEvent({ sectionKey })

  const { config } = useAlternateISIN(WaterfallSections.ELITE)

  if (config === WaterfallValues.NO) return null

  if (isUserElite) return <EliteCard sectionKey='ELITE_STATS' />

  return (
    <div ref={sectionRef} className='bg-white rounded-xl z-0 pb-[26px] flex flex-col w-full justify-center pt-[1px]'>
      <div className='pre-elite-header relative h-[300px] z-0 w-full'>
        <div className='h-full  rounded-[12px]  overflow-hidden'>
          <RiveComponent src={`${ELITE_RIVE_URL}/mf_elite_upsell.riv`} fit={RiveFit.FILL} />
        </div>
        <div className='flex w-full flex-col absolute top-0 z-10'>
          <div className='w-full mx-auto pt-6'>
            <h2 className='sr-only'>PowerUp Elite</h2>
            <img src='/images/mf-l2/pre-elite-header.svg' alt='' aria-hidden='true' className='mx-auto' />
          </div>
          <div className='w-full pt-8 pb-10'>
            <div className='flex justify-center mx-auto flex-row max-w-[234px] w-full'>
              <div aria-hidden='true'>
                <img src='/images/mf-l2/left-leaf.svg' alt='' />
              </div>
              <div className='px-[22px] text-[#722200] text-[13px] font-medium flex flex-col items-center'>
                <div className='pt-2'>Power Rank</div>
                <div className='pt-3'>
                  <RenderHTML innerHTMLText={FilledLockIcon} />
                </div>
              </div>
              <div aria-hidden='true'>
                <img src='/images/mf-l2/right-leaf.svg' alt='' />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='bg-white rounded-t-xl mt-[-110px] z-10'>
        <div className='flex flex-col w-full'>
          {features.map((feature, index) => (
            <EliteFeaturesRow
              key={feature.featureIcon}
              featureIcon={feature.featureIcon}
              featureName={getFeatureName(feature?.eliteScoresType)}
              helpContent={feature.helpContent}
              isLast={feature.isLast}
            />
          ))}
        </div>
      </div>
      <div className='px-5 pt-2'>
        <div
          onClick={() => {
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                buttonLabel: 'View all benefits',
                screenType: 'MF_L2'
              }
            })
            if (data?.eliteIconCta) postCTA(JSON.stringify(data?.eliteIconCta))
          }}
          className='bg-[#EBF2F2] text-black hover:text-black rounded-[12px] w-full h-12 text-[14px] font-medium text-center flex items-center justify-center'
        >
          View all Elite benefits
        </div>
      </div>
    </div>
  )
}
